import React, { useState, useEffect, useRef, useCallback } from "react";
import * as echarts from "echarts";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import Loader from "react-spinner-loader";
import { FullScreen, useFullScreenHandle } from "react-full-screen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faCity } from "@fortawesome/free-solid-svg-icons";
import { getData, postDataWithBody } from "../../../../Services/Services";
import {
  firedashboardcardsdata,
  firedashboardbarchart,
  firedashboardblockdata,
} from "../../../../Services/EndPoints";
import { dashboardCardConfigForFire } from "./DashboardConfigForFire";
import "react-datepicker/dist/react-datepicker.css";
import "./FireDashboard.css";

// ── Design tokens ──────────────────────────────────────────────────────
const C = {
  bg: "#F0F6FB", surface: "#FFFFFF", navBg: "#1C4E72",
  primary: "#2A6591", text1: "#0F2D42", text2: "#3A5F78",
  text3: "#7A9BB0", border: "rgba(42,101,145,.10)",
};
const PALETTE = [
  { bg: "#FFF0F0", color: "#D95555" },
  { bg: "#FFF4EA", color: "#D4772A" },
  { bg: "#EEFAF3", color: "#27A05A" },
  { bg: "#EBF4FF", color: "#3579D4" },
  { bg: "#FFF0F9", color: "#C94FA0" },
  { bg: "#F4EFFF", color: "#7E57C2" },
  { bg: "#E8FFF5", color: "#0F9D58" },
  { bg: "#FFF8EB", color: "#BF8C00" },
];

function FireIcon({ keyName, label, color, size = "16" }) {
  const k = (keyName + " " + label).toLowerCase();
  const sw = "1.5"; const r = "round";
  
  // Specific Human, Cattle, Poultry
  if (k.includes("human"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><circle cx="12" cy="7" r="4" stroke={color} strokeWidth={sw}/><path d="M5 21c0-3.5 3.5-6.5 7-6.5s7 3 7 6.5" stroke={color} strokeWidth={sw} strokeLinecap={r}/></svg>);
  if (k.includes("cattle") && !k.includes("shed"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M7 11c0-4 10-4 10 0v5L12 21l-5-5z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M7 11c-2-1-3-3-3-5" stroke={color} strokeWidth={sw} strokeLinecap={r}/><path d="M17 11c2-1 3-3 3-5" stroke={color} strokeWidth={sw} strokeLinecap={r}/></svg>);
  if (k.includes("poultry"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><circle cx="11" cy="11" r="5" stroke={color} strokeWidth={sw}/><path d="M11 16v5M8 21h6" stroke={color} strokeWidth={sw} strokeLinecap={r}/><path d="M16 11l4-2l-4-2" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><circle cx="12.5" cy="9.5" r=".5" fill={color}/></svg>);

  if (k.includes("death") || k.includes("loss"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><circle cx="12" cy="8" r="4" stroke={color} strokeWidth={sw}/><path d="M5.5 21v-1c0-3.5 3-6 6.5-6h0c3.5 0 6.5 2.5 6.5 6v1" stroke={color} strokeWidth={sw} strokeLinecap={r}/><path d="M9 6l6 6M15 6l-6 6" stroke={color} strokeWidth="1.2" strokeLinecap={r}/></svg>);
  if (k.includes("injur"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><circle cx="12" cy="7" r="4" stroke={color} strokeWidth={sw}/><path d="M6 21v-2c0-3.3 2.7-6 6-6s6 2.7 6 6v2" stroke={color} strokeWidth={sw} strokeLinecap={r}/><path d="M12 12v6" stroke={color} strokeWidth="1.2" strokeLinecap={r}/></svg>);
  if (k.includes("house") && k.includes("dam"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M9 22V12h6v10" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M6 8l12 12" stroke={color} strokeWidth="1.1" strokeLinecap={r}/></svg>);
  if (k.includes("cattle") && k.includes("shed"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><rect x="2" y="10" width="20" height="12" rx="1" stroke={color} strokeWidth={sw}/><path d="M1 10l11-7 11 7" stroke={color} strokeWidth={sw} strokeLinecap={r} strokeLinejoin={r}/></svg>);
  if (k.includes("crop"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M12 22v-9" stroke={color} strokeWidth={sw} strokeLinecap={r}/><path d="M12 13c-4 0-7-3-7-7 4 0 7 3 7 7z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M12 10c4 0 7-3 7-7-4 0-7 3-7 7z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/></svg>);
  if (k.includes("polythene") || k.includes("sheet"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><rect x="5" y="3" width="14" height="18" rx="2" stroke={color} strokeWidth={sw}/><path d="M9 8h6M9 12h6" stroke={color} strokeWidth={sw} strokeLinecap={r}/></svg>);
  if (k.includes("ration") || k.includes("dry"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M4 12c0-4.4 3.6-8 8-8s8 3.6 8 8" stroke={color} strokeWidth={sw} strokeLinecap={r}/><rect x="3" y="12" width="18" height="8" rx="2" stroke={color} strokeWidth={sw}/></svg>);
  if (k.includes("kitchen"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><rect x="3" y="10" width="18" height="11" rx="2" stroke={color} strokeWidth={sw}/><path d="M3 15h18" stroke={color} strokeWidth={sw}/><path d="M8 5v5M12 3v7M16 5v5" stroke={color} strokeWidth={sw} strokeLinecap={r}/></svg>);
  
  if (k.includes("concrete"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><rect x="6" y="4" width="12" height="18" rx="1" stroke={color} strokeWidth={sw}/><path d="M6 10h12M6 16h12M12 4v18" stroke={color} strokeWidth={sw}/></svg>);
  if (k.includes("kutcha"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M3 21V11l9-7 9 7v10" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M9 21v-6h6v6" stroke={color} strokeWidth={sw} strokeLinejoin={r}/></svg>);
  if (k.includes("cottage") || k.includes("hut"))
    return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M2 13l10-10 10 10" stroke={color} strokeWidth={sw} strokeLinecap={r} strokeLinejoin={r}/><path d="M5 12v9h14v-9" stroke={color} strokeWidth={sw} strokeLinejoin={r}/></svg>);
  // fallback fire
  return (<svg viewBox="0 0 24 24" fill="none" width={size} height={size}><path d="M12 3c0 5-5 7-5 12a5 5 0 0010 0c0-5-5-7-5-12z" stroke={color} strokeWidth={sw} strokeLinejoin={r}/><path d="M12 16a2 2 0 002-2c0-2-2-3-2-5 0 2-2 3-2 5a2 2 0 002 2z" stroke={color} strokeWidth="1.1" strokeLinejoin={r}/></svg>);
}

// ── Format helpers ─────────────────────────────────────────────────────
const TITLE_MAP = {
  no_of_deaths_count: "Number of Deaths", no_of_human_deaths_today: "Human Deaths",
  no_of_cattle_deaths_today: "Cattle Deaths", no_of_poultry_deaths_today: "Poultry Deaths",
  no_of_injured_count: "Number of Injured", no_of_human_injured_today: "Human Injured",
  no_of_cattle_injured_today: "Cattle Injured", no_of_poultry_injured_today: "Poultry Injured",
  no_of_damaged_houses: "Damaged Houses", concrete_count: "Concrete Damaged",
  concrete_complete: "Complete Concrete", concrete_partial: "Partial Concrete",
  kutcha_count: "Kutcha Damaged", kutcha_complete: "Complete Kutcha",
  kutcha_partial: "Partial Kutcha", cottage_hut: "Cottage / Hut",
  no_of_dam_cattle_sheds_today: "Cattle Sheds Damaged",
  damaged_crop_area_affected: "Crop Damaged (acres)",
  number_of_polythene_sheets_distributed_today: "Polythene Sheets",
  quantity_dry_ration_distributed_today: "Dry Ration (kg)",
  no_of_kitchens_run: "Kitchens Running",
};
const fmtTitle = k => TITLE_MAP[k] || (k || "").split("_").map(w => w[0]?.toUpperCase() + w.slice(1)).join(" ");
const fmtNum = v => v != null ? Number(v).toLocaleString("en-IN") : "0";

// ── Popup ──────────────────────────────────────────────────────────────
const DistrictPopup = ({ show, onHide, districtData, incidentType, blocks }) => {
  if (!show) return null;
  return (
    <div className="fd-popup-overlay" onClick={onHide}>
      <div className="fd-popup" onClick={e => e.stopPropagation()}>
        <div className="fd-popup-header">
          <div className="fd-popup-header-left">
            <div className="fd-popup-logo">
              <svg viewBox="0 0 20 20" fill="none" width="14" height="14"><path d="M10 2c0 4-4 6-4 10a4 4 0 008 0c0-4-4-6-4-10z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/></svg>
            </div>
            <div style={{ minWidth: 0 }}>
              <div className="fd-popup-overline">Fire Impact Zone</div>
              <div className="fd-popup-title-text">{districtData} • {fmtTitle(incidentType)}</div>
            </div>
          </div>
          <button className="fd-popup-close" onClick={onHide}>✕</button>
        </div>
        <div className="fd-popup-body">
          {blocks?.length > 0 ? blocks.map((b, i) => (
            <div key={i} className="fd-popup-row">
              <div className="fd-popup-row-left">
                <div className="fd-popup-row-num">{i + 1}</div>
                <FontAwesomeIcon icon={faCity} style={{ color: C.primary, fontSize: 11 }} />
                <span>{b.block_name}</span>
              </div>
              <div className="fd-popup-row-badge">{b.count.toLocaleString("en-IN")}</div>
            </div>
          )) : (
            <div className="fd-popup-empty">
              <FontAwesomeIcon icon={faInfoCircle} className="fd-popup-empty-icon" />
              <div className="fd-popup-empty-text">No block data available</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// ── MAIN DASHBOARD ─────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
const FireDashboard = () => {
  const handle = useFullScreenHandle();
  const [loader, setLoader] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [cardData, setCardData] = useState({});
  const [selectedIncident, setSelectedIncident] = useState("");
  const [popupState, setPopupState] = useState({ show: false, district: null, blocks: [], incidentType: null });
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isTrendMinimized, setIsTrendMinimized] = useState(false);
  const [selectedYear, setSelectedYear] = useState(2026);
  const [internalFromDate, setInternalFromDate] = useState(new Date(2026, 0, 1));
  const [internalToDate, setInternalToDate] = useState(new Date());
  const [appliedFromDate, setAppliedFromDate] = useState(new Date(2026, 0, 1));
  const [appliedToDate, setAppliedToDate] = useState(new Date());

  const startDateStr = appliedFromDate ? moment(appliedFromDate).format("YYYY-MM-DD") : "";
  const endDateStr = appliedToDate ? moment(appliedToDate).format("YYYY-MM-DD") : "";

  const fetchCardData = useCallback(async (from, to) => {
    setLoader(true); setError(null);
    try {
      const data = await getData(firedashboardcardsdata.replace("FROMDATE", moment(from).format("YYYY-MM-DD")).replace("TODATE", moment(to).format("YYYY-MM-DD")));
      setCardData(data?.[0] || {});
    } catch { setError("Failed to load data."); }
    finally { setLoader(false); }
  }, []);

  useEffect(() => { fetchCardData(appliedFromDate, appliedToDate); }, []); // eslint-disable-line

  const handleApply = () => {
    if (!internalFromDate || !internalToDate) { setError("Select both dates"); return; }
    if (internalFromDate > internalToDate) { setError("From must be before To"); return; }
    setError(null); setAppliedFromDate(internalFromDate); setAppliedToDate(internalToDate);
    fetchCardData(internalFromDate, internalToDate);
  };
  const handleYearChange = e => {
    const y = parseInt(e.target.value); setSelectedYear(y);
    const nf = new Date(y, 0, 1), ye = new Date(y, 11, 31), t = new Date(), nt = t <= ye ? t : ye;
    setInternalFromDate(nf); setInternalToDate(nt);
    setAppliedFromDate(nf); setAppliedToDate(nt);
    fetchCardData(nf, nt);
  };
  useEffect(() => {
    if (internalFromDate && internalToDate && internalFromDate > internalToDate) setError("Select proper dates");
    else setError(null);
  }, [internalFromDate, internalToDate]);
  const handleBarClick = async district => {
    try {
      const ids = district.region_id.split(",").map(Number);
      const bd = await postDataWithBody(firedashboardblockdata, { region_id_list: ids, from_date: startDateStr, to_date: endDateStr, bar_type: selectedIncident }, {});
      setPopupState({ show: true, district: district.dist_name, blocks: bd || [], incidentType: selectedIncident });
    } catch { setPopupState({ show: true, district: district.dist_name, blocks: [], incidentType: selectedIncident }); }
  };
  const toggleFullscreen = () => { isFullscreen ? handle.exit() : handle.enter(); setIsFullscreen(!isFullscreen); };

  const topCards = dashboardCardConfigForFire[0]?.data || [];

  // Top-level summary keys for the bottom charts
  const summaryKeys = topCards.filter(c => !c.subData || c.subData.length <= 3).map(c => ({ key: c.key, label: c.label }));

  return (
    <FullScreen handle={handle}>
      <div className={`fd-root${isFullscreen ? " fd-fs" : ""}`}>
        <Loader show={loader} type="body" stack="vertical" message="Loading Data" />

        {/* ── NAV ── */}
        <nav className="fd-nav">
          <div className="fd-nav-brand">
            <div className="fd-nav-logo">
              <svg viewBox="0 0 512 512" width="16" height="16" fill="white">
                <path d="M349.485 86.766c-25.569-21.67-51.906-44.15-78.561-70.72L255.731.96l-14.915 15.107c-47.985 48.113-70.656 121.987-80.5 166.307a128.6 128.6 0 0 1-16.556-37.97l-9.013-34.795-26.208 24.632C62.619 177.345 32 220.11 32 288.592c-.882 101.634 67.488 190.839 165.859 216.401a236.2 236.2 0 0 0 43.915 6.392c4.864.352 18.793.703 21.18.554 120.685-3.63 216.608-102.565 216.507-223.305 0-91.494-60.236-142.633-129.976-201.868m-89.627 382.282c-2.231.193-4.259.213-6.516.192-39.844-1.218-71.54-33.819-71.637-73.681 0-27.002 14.481-40.461 47.254-68.357 8.262-7.027 17.249-14.672 26.704-23.425 8.284 7.517 16.333 14.332 23.765 20.656 32.922 27.939 50.107 43.804 50.107 71.062-.074 39.104-30.634 71.364-69.677 73.553m106.901-37.714-.426.298a116.1 116.1 0 0 0 5.792-36.074c0-48.233-31.645-75.108-65.12-103.537-11.861-10.051-24.106-20.443-36.329-32.667l-15.077-15.056-15.056 15.056c-13.799 13.778-27.3 25.277-39.204 35.414-33.391 28.408-62.246 52.961-62.246 100.79a115.9 115.9 0 0 0 6.389 37.543 181.33 181.33 0 0 1-70.891-144.679 141.34 141.34 0 0 1 40.226-100.087 165 165 0 0 0 15.929 25.959 38.33 38.33 0 0 0 39.524 14.502 39.52 39.52 0 0 0 30.09-30.452 348.8 348.8 0 0 1 56.56-136.289c22.466 21.295 44.72 40.141 64.801 57.199 66.696 56.645 114.994 97.574 114.994 169.296a179.97 179.97 0 0 1-69.976 142.784z" />
              </svg>
            </div>
            <div className="fd-nav-title">Fire Incident Dashboard</div>
          </div>
          <div className="fd-nav-right">
            <div className="fd-nav-group">
              <span className="fd-nav-label">Year</span>
              <select className="fd-nav-select" value={selectedYear} onChange={handleYearChange}>
                <option value={2024}>2024</option><option value={2025}>2025</option><option value={2026}>2026</option>
              </select>
            </div>
            <div className="fd-daterange">
              <div className="fd-daterange-field">
                <span className="fd-dr-label">FROM</span>
                <ReactDatePicker selected={internalFromDate} onChange={d => setInternalFromDate(d)} dateFormat="dd/MM/yyyy" isClearable placeholderText="Select" todayButton="Today" className="fd-dp-input" />
              </div>
              <div className="fd-dr-divider" />
              <div className="fd-daterange-field">
                <span className="fd-dr-label">TO</span>
                <ReactDatePicker selected={internalToDate} onChange={d => setInternalToDate(d)} dateFormat="dd/MM/yyyy" isClearable placeholderText="Select" todayButton="Today" className="fd-dp-input" />
              </div>
            </div>
            <button className="fd-btn-apply" onClick={handleApply} disabled={!!error || !internalFromDate || !internalToDate}>Apply</button>
            <button className="fd-btn-icon" onClick={toggleFullscreen}>
              {isFullscreen
                ? <svg viewBox="0 0 18 18" fill="none" width="13" height="13"><path d="M2 6H6V2M16 6H12V2M2 12H6V16M16 12H12V16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
                : <svg viewBox="0 0 18 18" fill="none" width="13" height="13"><path d="M2 6V2h4M12 2h4v4M2 12v4h4M16 12v4h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>}
            </button>
          </div>
        </nav>

        {error && <div className="fd-error-strip"><svg viewBox="0 0 16 16" fill="none" width="12" height="12"><circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4"/><path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>{error}</div>}

        <div className="fd-content">
          {/* ── ROW 1: 3 Complex Cards (Deaths, Injured, Damaged Houses) ── */}
          <div className="fd-top-cards">
            {topCards.slice(0, 3).map((card, ci) => {
              const p = PALETTE[ci % PALETTE.length];
              return (
                <div key={card.key} className={`fd-top-premium-card ${selectedIncident === card.key ? 'active' : ''}`}>
                  <div
                    className="fd-top-hero"
                    onClick={() => setSelectedIncident(card.key)}
                  >
                    <div className="fd-top-hero-info">
                      <span className="fd-top-hero-title">{card.label}</span>
                      <span className="fd-top-hero-value">{fmtNum(cardData[card.key])}</span>
                    </div>
                    <div className="fd-top-hero-icon" style={{ background: p.bg, color: p.color }}>
                      <FireIcon keyName={card.key} label={card.label} color={p.color} size="20" />
                    </div>
                  </div>
                  
                  {card.subData && (
                    <div className="fd-top-stats-row">
                      {card.subData.map((sub, si) => {
                        const sp = PALETTE[(ci + si + 1) % PALETTE.length];
                        const hideMicro = !sub.subData || sub.subData.length === 0;
                        return (
                          <div
                            key={sub.key}
                            className={`fd-top-stat-item ${selectedIncident === sub.key ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setSelectedIncident(sub.key); }}
                          >
                            <div className="fd-top-stat-head">
                              <span className="fd-top-stat-icon" style={{ color: sp.color }}>
                                <FireIcon keyName={sub.key} label={sub.label} color={sp.color} size="14" />
                              </span>
                              <span className="fd-top-stat-lbl">{sub.label}</span>
                            </div>
                            <span className="fd-top-stat-val" style={{ marginBottom: hideMicro ? '4px' : '6px' }}>
                              {fmtNum(cardData[sub.key])}
                            </span>
                            
                            {sub.subData && (
                              <div className="fd-top-micro-row">
                                {sub.subData.map(n => (
                                  <div
                                    key={n.key}
                                    className={`fd-top-micro-badge ${selectedIncident === n.key ? 'active' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); setSelectedIncident(n.key); }}
                                    title={n.label}
                                  >
                                    <span className="fd-micro-lbl">{n.label.includes("Complete") ? "C:" : n.label.includes("Partial") ? "P:" : n.label[0] + ":"}</span>
                                    <span className="fd-micro-val">{fmtNum(cardData[n.key])}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── ROW 2: 5 Simple Cards + Bar Chart ── */}
          <div className="fd-main-row">
            {/* LEFT: 5 Simple Cards */}
            <div className="fd-cards-panel">
              {topCards.slice(3).map((card, ci) => {
                const globalIndex = ci + 3;
                const p = PALETTE[globalIndex % PALETTE.length];
                return (
                  <div key={card.key} className="fd-section-card">
                    <div
                      className={`fd-section-header${selectedIncident === card.key ? " fd-section-header--active" : ""}`}
                      onClick={() => setSelectedIncident(card.key)}
                    >
                      <span className="fd-card-icon" style={{ background: p.bg }}>
                        <FireIcon keyName={card.key} label={card.label} color={p.color} />
                      </span>
                      <span className="fd-section-label">{card.label}</span>
                      <span className="fd-section-value">{fmtNum(cardData[card.key])}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* RIGHT: Bar Chart */}
            <div className="fd-chart-panel">
              <div className={`fd-chart-wrap${isTrendExpanded ? " fd-chart-wrap--expanded" : ""}`}>
                <div className="fd-chart-top">
                  <div>
                    <h3 className="fd-chart-heading">{selectedIncident ? fmtTitle(selectedIncident) : "District-wise Trend"}</h3>
                    {!selectedIncident && <p className="fd-chart-range">Select any card to view its district breakdown</p>}
                    {selectedIncident && <p className="fd-chart-range">{moment(startDateStr).format("DD/MM/YYYY")} — {moment(endDateStr).format("DD/MM/YYYY")}</p>}
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    {selectedIncident && <button className="fd-badge-btn" onClick={() => setSelectedIncident("")}>{fmtTitle(selectedIncident)} ✕</button>}
                  </div>
                </div>
                {(isTrendExpanded || !isTrendMinimized) && (
                  <>
                    {!selectedIncident && (
                      <div className="fd-chart-empty">
                        <div className="fd-chart-empty-icon">
                          <svg viewBox="0 0 48 48" fill="none" width="36" height="36"><rect x="4" y="30" width="8" height="14" rx="2" fill="#C5DFF0"/><rect x="16" y="20" width="8" height="24" rx="2" fill="#A0C8E0"/><rect x="28" y="12" width="8" height="32" rx="2" fill="#7AB4D0"/><rect x="40" y="22" width="8" height="22" rx="2" fill="#5AA0C0"/></svg>
                        </div>
                        <p className="fd-chart-empty-text">Click any incident card to visualize its district breakdown</p>
                      </div>
                    )}
                    <div className="fd-chart-area">
                      <FireBarChartInner selectedIncident={selectedIncident} startDateStr={startDateStr} endDateStr={endDateStr} onBarClick={handleBarClick} isTrendExpanded={isTrendExpanded} />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── ROW 3: BOTTOM CHARTS ── */}
          <div className="fd-bottom-charts">
            <FirePieChart cardData={cardData} summaryKeys={summaryKeys} />
            <FireLineChart cardData={cardData} summaryKeys={summaryKeys} />
            <FireFunnelChart cardData={cardData} summaryKeys={summaryKeys} />
          </div>

          {/* ── Popup ── */}
          <DistrictPopup show={popupState.show} onHide={() => setPopupState({ show: false, district: null, blocks: [], incidentType: null })} districtData={popupState.district} incidentType={popupState.incidentType} blocks={popupState.blocks} />
        </div>
      </div>
    </FullScreen>
  );
};

// ═══════════════════════════════════════════════════════════════════════
// ── BAR CHART ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
function FireBarChartInner({ selectedIncident, startDateStr, endDateStr, onBarClick, isTrendExpanded }) {
  const ref = useRef(null), inst = useRef(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!selectedIncident || !startDateStr || !endDateStr) return;
    setLoading(true);
    getData(firedashboardbarchart.replace("FROMDATE", startDateStr).replace("TODATE", endDateStr).replace("BARTYPE", selectedIncident))
      .then(r => setData(r || [])).catch(console.error).finally(() => setLoading(false));
  }, [selectedIncident, startDateStr, endDateStr]);

  const valid = data.filter(i => i.count > 0).length;
  const h = "100%";

  useEffect(() => {
    if (!ref.current || !data.length) return;
    const sorted = [...data.filter(i => i.count > 0)].sort((a, b) => a.count - b.count);
    if (!sorted.length) return;
    const opt = {
      tooltip: { trigger: "axis", axisPointer: { type: "shadow" },
        formatter: p => `<div style="font-weight:700;color:${C.text1};border-bottom:1px solid ${C.border};margin-bottom:6px;padding-bottom:4px">${p[0].name}</div><span style="color:${C.text3}">Count: </span><span style="font-weight:700;color:${C.primary}">${p[0].value.toLocaleString("en-IN")}</span>`,
        backgroundColor: C.surface, borderColor: C.border, borderWidth: 1,
        extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);font-family:'Inter',sans-serif;",
      },
      grid: { left: "15%", right: "10%", bottom: 36, top: 20, containLabel: false },
      xAxis: { type: "value", axisLabel: { color: C.text3, fontSize: 11 }, splitLine: { lineStyle: { color: C.border, type: "dashed" } }, axisTick: { show: false } },
      yAxis: { type: "category", data: sorted.map(i => i.dist_name), axisLabel: { color: C.text1, fontSize: 11, fontWeight: "600" }, axisLine: { lineStyle: { color: C.border } } },
      series: [{ type: "bar",
        data: sorted.map(item => ({ value: item.count, itemStyle: { color: { type: "linear", x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: "#A0C8E0" }, { offset: 1, color: C.primary }] }, borderRadius: [0, 8, 8, 0] } })),
        barWidth: 14, barMaxWidth: 18, barCategoryGap: "20%",
        label: { show: true, position: "right", formatter: p => p.value.toLocaleString("en-IN"), fontSize: 10, fontWeight: "700", color: C.primary },
        showBackground: true, backgroundStyle: { color: "rgba(42,101,145,.04)", borderRadius: [0, 8, 8, 0] },
      }],
      dataZoom: sorted.length > 6 ? [{ type: "slider", yAxisIndex: 0, start: 0, end: 60, orient: "vertical", handleStyle: { color: C.primary }, borderColor: C.border, backgroundColor: C.surface }] : [],
    };
    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(opt);
    inst.current.on("click", p => { if (p.componentType === "series") { const d = data.find(x => x.dist_name === p.name); if (d && onBarClick) onBarClick(d); } });
    const onR = () => inst.current?.resize();
    window.addEventListener("resize", onR);
    return () => { window.removeEventListener("resize", onR); inst.current?.dispose(); };
  }, [data, selectedIncident, isTrendExpanded]);

  if (!selectedIncident) return null;
  if (loading) return <div style={{ textAlign: "center", padding: 40 }}><Loader show type="body" stack="vertical" message="Analyzing..." /></div>;
  if (!data.length || !data.some(i => i.count > 0)) return <div style={{ textAlign: "center", padding: 40, color: C.text3, fontSize: 12 }}>No data for the selected period</div>;
  return <div ref={ref} style={{ width: "100%", height: h }} />;
}

// ═══════════════════════════════════════════════════════════════════════
// ── PIE CHART ──────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
function FirePieChart({ cardData, summaryKeys }) {
  const ref = useRef(null), inst = useRef(null);
  const blues = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0", "#7AB4D0", "#A0C8E0", "#C5DFF0", "#DCEdf5"];
  useEffect(() => {
    if (!ref.current) return;
    const sd = summaryKeys.map((c, i) => ({ name: fmtTitle(c.key), value: cardData[c.key] || 0, itemStyle: { color: blues[i % blues.length] } })).filter(d => d.value > 0);
    const opt = {
      title: { text: "IMPACT DISTRIBUTION", left: "center", top: 14, textStyle: { color: C.text1, fontSize: 11, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: { trigger: "item", formatter: p => `<div style="padding:3px"><div style="font-weight:700;color:${C.text1};margin-bottom:3px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:16px">${p.value.toLocaleString("en-IN")}</div><div style="color:${C.text3};font-size:10px">${p.percent.toFixed(1)}%</div></div>`, backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);" },
      legend: { orient: "horizontal", bottom: 10, itemWidth: 8, itemHeight: 8, itemGap: 12, textStyle: { color: C.text3, fontSize: 9, fontWeight: "600" } },
      series: [{ type: "pie", radius: ["35%", "60%"], center: ["50%", "48%"], label: { show: false }, emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,.3)' } }, data: sd }],
    };
    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(opt);
    const onR = () => inst.current?.resize();
    window.addEventListener("resize", onR);
    return () => { window.removeEventListener("resize", onR); inst.current?.dispose(); };
  }, [cardData, summaryKeys]);
  return <div className="fd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

// ═══════════════════════════════════════════════════════════════════════
// ── LINE CHART ─────────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
function FireLineChart({ cardData, summaryKeys }) {
  const ref = useRef(null), inst = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const opt = {
      title: { text: "CATEGORY COMPARISON", left: "center", top: 14, textStyle: { color: C.text1, fontSize: 11, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: { trigger: "axis", backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);" },
      grid: { left: "10%", right: "10%", bottom: "18%", top: "22%", containLabel: true },
      xAxis: { type: "category", boundaryGap: false, data: summaryKeys.map(c => fmtTitle(c.key)), axisLabel: { color: C.text3, fontSize: 8, rotate: 20 }, axisLine: { lineStyle: { color: C.border } } },
      yAxis: { type: "value", axisLabel: { color: C.text3, fontSize: 10 }, splitLine: { lineStyle: { color: C.border, type: "dashed" } } },
      series: [{ data: summaryKeys.map(c => cardData[c.key] || 0), type: "line", smooth: true, symbolSize: 7, itemStyle: { color: C.primary }, lineStyle: { width: 2.5, color: C.primary }, areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: "rgba(42,101,145,0.25)" }, { offset: 1, color: "rgba(42,101,145,0)" }]) } }],
    };
    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(opt);
    const onR = () => inst.current?.resize();
    window.addEventListener("resize", onR);
    return () => { window.removeEventListener("resize", onR); inst.current?.dispose(); };
  }, [cardData, summaryKeys]);
  return <div className="fd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

// ═══════════════════════════════════════════════════════════════════════
// ── FUNNEL CHART ───────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════
function FireFunnelChart({ cardData, summaryKeys }) {
  const ref = useRef(null), inst = useRef(null);
  const blues = ["#1C4E72", "#2A6591", "#3A85B5", "#5AA0C0", "#7AB4D0", "#A0C8E0", "#C5DFF0", "#DCEdf5"];
  useEffect(() => {
    if (!ref.current) return;
    const sd = summaryKeys.map((c, i) => ({ name: fmtTitle(c.key), value: cardData[c.key] || 0, itemStyle: { color: blues[i % blues.length] } })).sort((a, b) => b.value - a.value);
    const opt = {
      title: { text: "IMPACT ANALYSIS", left: "center", top: 14, textStyle: { color: C.text1, fontSize: 11, fontWeight: "700", fontFamily: "'Sora',sans-serif" } },
      tooltip: { trigger: "item", formatter: p => `<div style="padding:3px"><div style="font-weight:700;color:${C.text1};margin-bottom:3px">${p.name}</div><div style="color:${C.primary};font-weight:800;font-size:16px">${p.value.toLocaleString("en-IN")}</div></div>`, backgroundColor: C.surface, borderColor: C.border, borderWidth: 1, extraCssText: "border-radius:12px;box-shadow:0 4px 16px rgba(15,45,66,.1);" },
      series: [{ name: "Fire Impact", type: "funnel", left: "15%", top: 55, bottom: 30, width: "70%", min: 0, minSize: "0%", maxSize: "100%", sort: "descending", gap: 2, label: { show: true, position: "inside", fontSize: 9, fontWeight: "700", color: "#fff", formatter: "{b}" }, labelLine: { show: false }, itemStyle: { borderColor: "#fff", borderWidth: 1 }, emphasis: { label: { fontSize: 11 } }, data: sd }],
    };
    if (inst.current) inst.current.dispose();
    inst.current = echarts.init(ref.current);
    inst.current.setOption(opt);
    const onR = () => inst.current?.resize();
    window.addEventListener("resize", onR);
    return () => { window.removeEventListener("resize", onR); inst.current?.dispose(); };
  }, [cardData, summaryKeys]);
  return <div className="fd-chart-cell"><div ref={ref} style={{ width: "100%", height: "100%" }} /></div>;
}

export default FireDashboard;
